import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { HoursReport } from "@/api/types";

export type HoursReportStatus = "loading" | "ready" | "error";

export interface UseHoursReportResult {
  /** `null` until resolved and on failure — never an empty report. A report with no lines
   *  is a real, different answer ("this farm has nobody on its roster"), and collapsing
   *  "we do not know yet" into it would render a confident 0.00 for every column. */
  report: HoursReport | null;
  status: HoursReportStatus;
  error: string | null;
  /** Fetch a specific window. Passing nothing asks the server for the current pay period. */
  load: (range?: { from: string; to: string }) => Promise<void>;
}

/**
 * The owner's hours report — GET /api/demo/time/report.
 *
 * Owner-only SERVER-side (403 for an employee login). This hook does not check that and
 * must not, for the same reason usePendingTime does not: a client-side role check is a
 * rendering decision, and treating it as the control is how the real one stops being
 * maintained.
 *
 * UNLIKE the roster hooks, this one is re-fetched on demand rather than once per mount —
 * changing the window IS the feature. The initial load deliberately passes no range, so
 * the first thing on screen is the period the server derived rather than one this client
 * guessed; the picker is then seeded from what came back.
 *
 * A failure is NOT degraded to an empty report. There is no honest version of this screen
 * without figures, and zero hours for the whole roster is exactly what a farmer would read
 * as "nobody worked" rather than "the request failed".
 */
export function useHoursReport(): UseHoursReportResult {
  const [report, setReport] = useState<HoursReport | null>(null);
  const [status, setStatus] = useState<HoursReportStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const fetchInto = useCallback(
    async (range?: { from: string; to: string }, signal?: AbortSignal): Promise<void> => {
      setStatus("loading");
      try {
        const resolved = await demoApi.getApprovedHoursReport(range, signal);
        if (signal?.aborted) return;
        setReport(resolved);
        setStatus("ready");
        setError(null);
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        // The PREVIOUS report is cleared, not kept. Leaving the old figures on screen under
        // the new dates the user just typed is the worst of the three options: it looks like
        // an answer to the question they asked.
        setReport(null);
        setStatus("error");
        setError(problemMessage(err, "Could not load the hours report."));
      }
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchInto(undefined, controller.signal);
    return () => controller.abort();
  }, [fetchInto]);

  const load = useCallback(
    (range?: { from: string; to: string }) => fetchInto(range),
    [fetchInto],
  );

  return { report, status, error, load };
}
